import { css } from '@understory-io/leaf/css';
import { useNavigation } from 'react-router';

/**
 * A slim bar across the top of the storefront while a navigation is pending.
 *
 * The router keeps the current page on screen until the next page's loaders
 * settle, so without a cue of its own a click — on an experience tile, say —
 * looks as if it never landed. The bar takes the tenant's accent colour, the
 * one its buttons use, so it reads as the storefront's own.
 *
 * It never completes on its own: it eases towards the end and goes away when
 * the next page renders, which is the completion a visitor sees anyway.
 */
export function NavigationProgress() {
  const navigation = useNavigation();
  if (navigation.state === 'idle') return null;

  // Keyed by the pending location, so a navigation that replaces another
  // starts its bar from the beginning rather than carrying on the old one.
  return (
    <div aria-hidden className={barStyles} key={navigation.location.key} />
  );
}

const barStyles = css({
  position: 'fixed',
  top: '[0]',
  left: '[0]',
  right: '[0]',
  // No `sizes` token is this thin; the bar has to be noticeable without
  // covering the header.
  height: '[3px]',
  // Above everything a navigation can start from, the preview banner (200)
  // included.
  zIndex: '[300]',
  background: 'interactive.accent.default',
  transformOrigin: 'left',
  pointerEvents: 'none',
  // The fade-in waits a beat: a navigation that settles at once (a filter
  // change that skips its loaders) would otherwise flash a bar for a frame.
  animation:
    'navigation-progress-in 150ms ease-out 100ms both, navigation-progress-grow 10s ease-out both',
  _motionReduce: {
    animation: 'navigation-progress-in 150ms ease-out 100ms both',
  },
});
